
// app/host-redirect.guard.ts
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { HostRouteConfig, HostRule } from '@core/services/host-route-map/host-route-config.service';
import { HOST_ROUTE_CONFIG } from '@core/services/host-route-map/host-route-map.token';


// SSR opcional
// import { PLATFORM_ID, isPlatformServer } from '@angular/core';
// import { REQUEST } from '@angular/ssr';
// import type { IncomingMessage } from 'http';

@Injectable({ providedIn: 'root' })
export class HostRedirectGuard implements CanActivate, CanActivateChild {

  // SSR opcional
  // private platformId = inject(PLATFORM_ID);
  // private request = inject<IncomingMessage | null>(REQUEST, { optional: true });

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Inject(HOST_ROUTE_CONFIG) private hostConfig: HostRouteConfig,
  ) { }




  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.resolveOrRedirect(state);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.resolveOrRedirect(state);
  }


  private resolveOrRedirect(state: RouterStateSnapshot): boolean | UrlTree {
    const host = this.getHost();
    const target = this.resolveRouteForHost(host);
    const currentUrl = this.router.url;

    // Já estamos navegando para o target? Libera
    if (state.url === target || state.url.startsWith(target + '/')) {
      return true;
    }

    // Redireciona apenas na entrada do sistema (URL vazia ou '/')
    if (state.url === '' || state.url === '/' || currentUrl === '' || currentUrl === '/') {
      return this.router.parseUrl(target);
    }



    // Em qualquer outro caso, não force redirecionamento (evita quebrar deep-links e loops)
    return true;
  }





  /** Obtém hostname sem porta, em minúsculas */
  private getHost(): string {
    // SSR opcional
    // if (isPlatformServer(this.platformId) && this.request) {
    //   return (this.request.headers?.host ?? '').toLowerCase().split(':')[0];
    // }

    return this.document.location.hostname.toLowerCase();
  }

  /** Resolve a rota com prioridade: exact → regex → prefix → suffix → default */
  private resolveRouteForHost(host: string): string  {
    const { rules } = this.hostConfig;
    if (!rules?.length) return '';

    console.log(`========== Host route redirect: ${host}`);

    const exactRules = rules.filter(r => r.type === 'exact') as Extract<HostRule, { type: 'exact' }>[];
    const regexRules = rules.filter(r => r.type === 'regex') as Extract<HostRule, { type: 'regex' }>[];
    const prefixRules = rules.filter(r => r.type === 'prefix') as Extract<HostRule, { type: 'prefix' }>[];
    const suffixRules = rules.filter(r => r.type === 'suffix') as Extract<HostRule, { type: 'suffix' }>[];

    // 1) exact
    for (const r of exactRules) {
      if (host === r.value.toLowerCase()) return r.route;
    }

    // 2) regex (com suporte a routeTemplate usando grupos nomeados)
    for (const r of regexRules) {
      try {
        const re = new RegExp(r.value, 'i');
        const m = host.match(re);
        if (m) {
          if (r.routeTemplate) {
            const route = this.applyTemplate(r.routeTemplate, m);
            if (route) return route;
          }
          if (r.route) return r.route;
        }
      } catch {
        // ignora regex inválida
      }
    }

    // 3) prefix
    for (const r of prefixRules) {
      const v = r.value.toLowerCase();
      if (host.startsWith(v)) return r.route;
    }

    // 4) suffix (normaliza para comparar fim do host)
    for (const r of suffixRules) {
      const v = r.value.toLowerCase();
      if (host.endsWith(v)) return r.route;
    }

    return '';
  }

  /** Interpola grupos nomeados da regex no template: '/t/{tenant}/home' */
  private applyTemplate(template: string, match: RegExpMatchArray): string | null {
    //groups pode não existir em algumas imagens TS, checamos abaixo
    const groups: Record<string, string> | undefined = match.groups;
    if (!groups) return null;

    return template.replace(/\{(\w+)\}/g, (_, g: string) => {
      const val = groups[g];
      return val ?? '';
    });
  }


}

